import { useState } from "react";
import { Bookmark, Lock, Globe, Plus, X, Check, Loader2 } from "lucide-react";
import useSave from "../../hooks/useSave";

const SAVE_COLOR = '#f97316';

const SaveButton = ({ postId, isBookmarked = false }) => {
  const {
    open, boards, loading, saving, creating,
    savedBoardId, newBoardName, isPrivate, error, saved,
    openModal, closeModal, handleSaveToBoard, handleCreateBoard,
    setNewBoardName, setIsPrivate,
  } = useSave(postId, isBookmarked);

  const [hovered, setHovered] = useState(false);
  const active = saved || hovered;

  return (
    <>
      <button
        onClick={openModal}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className="flex items-center gap-1.5 p-2 rounded-full
          transition-all duration-300 hover:scale-110 active:scale-95"
      >
        <Bookmark
          className="w-6 h-6 transition-all duration-300"
          style={{
            color: active ? SAVE_COLOR : 'rgb(243,244,246)',
            fill: saved ? SAVE_COLOR : 'transparent',
            filter: saved ? `drop-shadow(0 0 6px ${SAVE_COLOR})` : 'none',
          }}
        />
      </button>

      {open && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[70]
            flex justify-center items-end"
          onClick={(e) => e.target === e.currentTarget && closeModal()}
        >
          <div className="bg-[#1a1a1a] w-full max-w-lg text-white
            rounded-t-3xl p-6 max-h-[75vh] overflow-y-auto
            animate-slideUp border-t border-white/10">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">Save to board</h2>
              <button onClick={closeModal}
                className="p-1.5 rounded-full hover:bg-white/10 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30
                rounded-xl text-red-400 text-sm">{error}</div>
            )}

            <div className="mb-6">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : boards.length === 0 ? (
                <p className="text-gray-500 text-center py-6 text-sm">
                  No boards yet — create one below!
                </p>
              ) : (
                boards.map((board) => (
                  <button key={board.id} onClick={() => handleSaveToBoard(board.id)}
                    disabled={saving}
                    className={`w-full p-4 rounded-xl mb-2 flex justify-between items-center
                      transition-all duration-200
                      ${savedBoardId === board.id
                        ? "border"
                        : "bg-white/5 hover:bg-white/10 border border-white/10"
                      }
                      ${saving ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                    style={savedBoardId === board.id
                      ? { background: `${SAVE_COLOR}18`, borderColor: `${SAVE_COLOR}40` }
                      : {}}
                  >
                    <span className="font-medium">{board.name}</span>
                    <div className="flex items-center gap-2">
                      {savedBoardId === board.id
                        ? <Check className="w-4 h-4" style={{ color: SAVE_COLOR }} />
                        : board.is_private
                          ? <Lock className="w-4 h-4 text-gray-400" />
                          : <Globe className="w-4 h-4 text-gray-400" />
                      }
                    </div>
                  </button>
                ))
              )}
            </div>

            <div className="border-t border-white/10 pt-5">
              <h3 className="text-base font-semibold mb-3 flex items-center gap-2">
                <Plus className="w-4 h-4" /> Create new board
              </h3>
              <input type="text" value={newBoardName}
                onChange={(e) => setNewBoardName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCreateBoard()}
                placeholder="Board name"
                className="w-full p-3 bg-white/5 border border-white/10 rounded-xl mb-3
                  outline-none text-white placeholder-gray-500 focus:border-white/30 transition-colors"
              />
              <button onClick={() => setIsPrivate(!isPrivate)}
                className={`flex items-center gap-2 mb-4 px-3 py-2 rounded-lg
                  transition-colors text-sm
                  ${isPrivate ? "bg-white/10 text-white" : "text-gray-400 hover:text-white"}`}
              >
                {isPrivate ? <Lock className="w-4 h-4" /> : <Globe className="w-4 h-4" />}
                {isPrivate ? "Private board" : "Public board"}
              </button>
              <button onClick={handleCreateBoard}
                disabled={!newBoardName.trim() || creating}
                className={`w-full py-3 rounded-xl font-medium text-sm
                  flex items-center justify-center gap-2 transition-all duration-200
                  ${!newBoardName.trim() || creating
                    ? "bg-white/10 text-gray-500 cursor-not-allowed"
                    : "bg-white text-black hover:bg-gray-100 active:scale-95"
                  }`}
              >
                {creating ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating...</> : "Create board"}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .animate-slideUp {
          animation: slideUp 0.3s cubic-bezier(0.32, 0.72, 0, 1);
        }
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </>
  );
};

export default SaveButton;
